import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import type {
  BoletoDoPainel,
  EstadoDoEnvio,
  TentativaDeEnvio,
} from "@/api/contrato";
import { PilulaDeSituacao } from "@/componentes/PilulaDeSituacao";
import { Dica } from "@/componentes/Dica";
import { contagem, dataHora, idCurto } from "@/formato";
import { rotaDoBoleto } from "@/rotas";

/*
 * O envio ao provider, como a tela o mostra (change `fix-emissao-ambigua`).
 *
 * `situacao` diz o estado do título, e `envio_estado` diz o que sabemos da nossa tentativa.
 * Durante todo o envio em aberto o boleto segue `ENFILEIRADO`, e sem isto a tela não separa
 * quem está na fila de quem pode ter título vivo.
 *
 * `Record` fecha os catálogos no compilador: estado novo sem rótulo reprova aqui. Uma resposta
 * mais nova que o painel ainda pode trazer valor que ele não conhece, e por isso a leitura
 * devolve cru o que não está no catálogo.
 */
const ROTULOS_DO_ENVIO: Record<EstadoDoEnvio, string> = {
  sem_envio: "Sem envio",
  em_voo: "Em voo",
  confirmado: "Confirmado",
  recusado: "Recusado",
  incerto: "Incerto",
  divergente: "Divergente",
  reenvio_autorizado: "Reenvio autorizado",
};

export const OPCOES_DE_ENVIO = Object.entries(ROTULOS_DO_ENVIO).map(
  ([valor, rotulo]) => [valor, rotulo] as const,
);

function rotuloDe(rotulos: Readonly<Record<string, string>>, valor: string): string {
  return Object.hasOwn(rotulos, valor) ? (rotulos[valor] ?? valor) : valor;
}

export function rotuloDoEnvio(estado: string): string {
  return rotuloDe(ROTULOS_DO_ENVIO, estado);
}

const ROTULOS_DA_CLASSE: Readonly<Record<string, string>> = {
  transitorio: "Transitória",
  permanente_negocio: "Dado recusado",
  permanente_config: "Configuração",
  conflito: "Conflito de número",
};

export function rotuloDaClasse(classe: string): string {
  return rotuloDe(ROTULOS_DA_CLASSE, classe);
}

const ROTULOS_DO_DESFECHO: Readonly<Record<string, string>> = {
  confirmado: "Confirmado",
  adotado: "Adotado",
  recusado: "Recusado",
  nao_saiu: "Não saiu",
  desistiu: "Desistiu",
  incerto: "Incerto",
  divergente: "Divergente",
};

/*
 * O que o estado significa para quem olha, onde ele não é rotina. O nome do estado está na
 * caixa do resumo, e esta frase diz só a consequência.
 */
const AVISOS_DO_ENVIO: Readonly<Partial<Record<string, string>>> = {
  incerto: "Pode existir título no provider, e só a sonda decide.",
  divergente: "O título achado sob este número não é deste boleto.",
};

export function avisoDoEnvio(estado: string): string | undefined {
  return AVISOS_DO_ENVIO[estado];
}

/** Estado normal não se anuncia: `confirmado` nunca, e `sem_envio` só quando o boleto falhou. */
export function envioForaDoNormal(boleto: Pick<BoletoDoPainel, "envio_estado" | "situacao">) {
  if (boleto.envio_estado === "confirmado") return false;
  return boleto.envio_estado !== "sem_envio" || boleto.situacao === "FALHA";
}

/** Os estados que a lista marca: quem exige atenção, numa varredura de duzentas linhas. */
const MARCADOS: Readonly<Partial<Record<string, string>>> = {
  incerto: "Envio incerto",
  divergente: "Envio divergente",
  reenvio_autorizado: "Reenvio autorizado",
};

/** A marca na linha da lista. Sem nada na tela para o boleto normal. */
export function MarcaDoEnvio({ estado }: { estado: string }) {
  const texto = MARCADOS[estado];
  if (texto === undefined) return null;

  return (
    // `z-[1]` sobe acima da camada do link da linha: embaixo dela, o `title` nunca abre.
    <span className="relative z-[1] inline-flex shrink-0 text-indeterminado" title={texto}>
      <AlertTriangle aria-hidden="true" className="size-3.5" />
      <span className="sr-only">{texto}</span>
    </span>
  );
}

/**
 * Os boletos vivos parecidos com este. Antes de confirmar, e no resumo: duplicidade de boleto é
 * o risco existencial do produto, e a lista não pode depender de abrir o diálogo.
 *
 * O diálogo não leva link: sair dali no meio da confirmação perderia a justificativa.
 *
 * `demais` é o gate do detalhe: há parecido fora da lista, e o operador não consegue declarar
 * todos. A contagem diz "mais de", porque a API só devolve os dez primeiros.
 */
export function PossiveisDuplicidades({
  parecidos,
  comLink = false,
  confirmando = false,
  demais = false,
}: {
  parecidos: readonly BoletoDoPainel[];
  comLink?: boolean;
  confirmando?: boolean;
  demais?: boolean;
}) {
  if (parecidos.length === 0) return null;

  const dica = demais
    ? "A lista mostra até dez. O que não aparece não dá para conferir, e o caso é de investigar."
    : confirmando
      ? "Reprocessar declara que você conferiu cada um, e a lista vai para a auditoria."
      : "Vivos, nesta aplicação, com mesmo sacado, valor e vencimento, ou com a mesma referência.";

  return (
    <section
      aria-label="Possível duplicidade"
      className="rounded-controle border border-indeterminado/40 bg-indeterminado/10 px-3 py-2"
    >
      <div className="flex items-center gap-1">
        <span className="rotulo text-indeterminado">
          {demais
            ? `Mais de ${parecidos.length} boletos parecidos`
            : contagem(parecidos.length, "boleto parecido", "boletos parecidos")}
        </span>
        <Dica texto={dica} />
      </div>

      <ul
        tabIndex={0}
        aria-label="Boletos parecidos"
        className="mt-1 flex max-h-36 flex-col gap-1 overflow-y-auto"
      >
        {parecidos.map((parecido) => {
          const referencia = parecido.referencia_externa ?? idCurto(parecido.id);

          return (
            <li key={parecido.id} className="flex items-center gap-2 text-[12px]">
              <PilulaDeSituacao situacao={parecido.situacao} />
              {comLink ? (
                <Link
                  to={rotaDoBoleto(parecido.id)}
                  title={parecido.id}
                  className="dados truncate text-esferografica underline-offset-2 hover:underline"
                >
                  {referencia}
                </Link>
              ) : (
                <span className="dados truncate text-texto" title={parecido.id}>
                  {referencia}
                </span>
              )}
              <span className="dados ml-auto shrink-0 text-texto-2">
                {dataHora(parecido.criado_em)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** As tentativas registradas, na aba técnica. Sem dado do sacado. */
export function TentativasDoEnvio({ tentativas }: { tentativas: readonly TentativaDeEnvio[] }) {
  if (tentativas.length === 0) return null;

  return (
    <section aria-label="Tentativas de envio" className="flex flex-col gap-1">
      <span className="rotulo">{contagem(tentativas.length, "tentativa", "tentativas")}</span>

      <div className="overflow-hidden rounded-controle border border-fio bg-superficie-2">
        <table className="w-full table-fixed text-left text-[12px]">
          <thead>
            <tr className="rotulo border-b border-fio">
              <th scope="col" className="w-8 px-2 py-1 font-medium">
                #
              </th>
              <th scope="col" className="w-20 px-2 py-1 font-medium">
                Número
              </th>
              <th scope="col" className="w-24 px-2 py-1 font-medium">
                Desfecho
              </th>
              <th scope="col" className="w-32 px-2 py-1 font-medium">
                Quando
              </th>
              <th scope="col" className="px-2 py-1 font-medium">
                Provider
              </th>
            </tr>
          </thead>
          <tbody>
            {tentativas.map((tentativa) => {
              const provider =
                tentativa.provider_id ??
                tentativa.provider_id_informado ??
                tentativa.falha_codigo ??
                "—";

              return (
                <tr key={tentativa.tentativa} className="border-b border-fio last:border-b-0">
                  <td className="dados px-2 py-1 text-texto-2">{tentativa.tentativa}</td>
                  <td className="dados truncate px-2 py-1 text-texto">{tentativa.nosso_numero}</td>
                  <td className="truncate px-2 py-1 text-texto">
                    {tentativa.desfecho === undefined
                      ? "Em aberto"
                      : rotuloDe(ROTULOS_DO_DESFECHO, tentativa.desfecho)}
                  </td>
                  <td className="dados truncate px-2 py-1 text-texto-2">
                    {dataHora(tentativa.desfecho_em ?? tentativa.criado_em)}
                  </td>
                  <td className="dados truncate px-2 py-1 text-texto-2" title={provider}>
                    {provider}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
